import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { api, LlmAreaRow, PricingTable } from '../services/api';

// Modelo de IA de cada área (CRM, Seller, Marketing Studio, Criar Conteúdo IA), e o provedor
// (prefixo do OmniRoute) de cada uma: o texto troca só a parte antes da 1ª "/" do modelo
// (cx/gpt-6-sol-medium + "codex" → codex/gpt-6-sol-medium).
// "Padrão" = variável da stack da área ou o modelo que ela já usava. O preço das funcionalidades
// com IA acompanha o modelo escolhido (tokens médios × tarifa da família na tabela de custos), e as
// chamadas cobradas por token também. Id livre (fora da lista) exige "Cobrar como": a família de
// preço que ele usa na tabela, para não rodar sem preço. Backend: services/llmAreaConfig.ts.

const CUSTOM = '__custom__';

const GROUP_LABELS: Record<LlmAreaRow['group'], string> = {
  crm: 'CRM',
  seller: 'Seller',
  marketing: 'Marketing Studio',
  criar_conteudo: 'Criar Conteúdo IA',
};
const GROUP_ORDER: LlmAreaRow['group'][] = ['crm', 'seller', 'marketing', 'criar_conteudo'];

// Mesma regra do backend (llmAreaConfig.applyPrefix), para mostrar o id final.
function applyPrefix(model: string, prefix: string) {
  const m = String(model || '').trim();
  const p = String(prefix || '').trim().replace(/^\/+|\/+$/g, '');
  if (!m || !p) return m;
  const i = m.indexOf('/');
  return `${p}/${i >= 0 ? m.slice(i + 1) : m}`;
}
function stripPrefix(model: string) {
  const i = String(model || '').indexOf('/');
  return i >= 0 ? model.slice(i + 1) : model;
}

export function LlmAreaModels({ table, onSaved }: { table: PricingTable; onSaved: () => Promise<void> | void }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [prefixes, setPrefixes] = useState<Record<string, string>>({});
  const [allPrefix, setAllPrefix] = useState('');
  const [custom, setCustom] = useState<Record<string, boolean>>({});
  const [modelFams, setModelFams] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(Object.fromEntries(table.areas.map(a => [a.key, a.adminValue || ''])));
    setPrefixes(Object.fromEntries(table.areas.map(a => [a.key, a.prefix || ''])));
    setModelFams({ ...(table.modelFamilies || {}) });
    setCustom({});
  }, [table]);

  const knownIds = new Set(table.modelOptions.map(o => o.id));
  const isCustom = (a: LlmAreaRow) => !!custom[a.key] || (!!values[a.key] && !knownIds.has(values[a.key]));
  const customId = (a: LlmAreaRow) => (isCustom(a) ? (values[a.key] || '').trim() : '');
  const familyDirty = (a: LlmAreaRow) => !!customId(a) && (modelFams[customId(a)] || '') !== ((table.modelFamilies || {})[customId(a)] || '');
  const missingFamily = table.areas.filter(a => customId(a) && !modelFams[customId(a)]);

  const modelDirty = (a: LlmAreaRow) => (values[a.key] || '') !== (a.adminValue || '');
  const prefixDirty = (a: LlmAreaRow) => (prefixes[a.key] || '').trim() !== (a.prefix || '');
  const dirty = table.areas.some(a => modelDirty(a) || prefixDirty(a) || familyDirty(a));

  // Modelo que vai valer com o rascunho: escolha (ou o atual sem o prefixo antigo) + provedor.
  const finalModel = (a: LlmAreaRow) => {
    const chosen = values[a.key] || (a.prefix ? stripPrefix(a.model) : a.model);
    return applyPrefix(chosen, prefixes[a.key] || '');
  };
  const families = Array.from(new Set(table.modelOptions.map(o => o.family)));

  const save = async () => {
    setSaving(true);
    try {
      const changed = Object.fromEntries(table.areas.filter(a => modelDirty(a) || familyDirty(a)).map(a => [a.key, (values[a.key] || '').trim()]));
      const changedFamilies = Object.fromEntries(table.areas.filter(customId).map(a => [customId(a), modelFams[customId(a)] || '']));
      const changedPrefixes = Object.fromEntries(table.areas.filter(prefixDirty).map(a => [a.key, (prefixes[a.key] || '').trim()]));
      await api.updateLlmAreas(changed, changedPrefixes, changedFamilies);
      toast.success('Modelos salvos. Valem em até 1 minuto.');
      await onSaved();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao salvar modelos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Modelos de IA por área</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Escolha o modelo de cada área. O preço em créditos das funcionalidades com IA segue o modelo escolhido.
            "Padrão" usa a variável da stack ou o modelo de hoje. Em "Provedor" você escreve o prefixo do OmniRoute
            (cx, codex, kc…): ele troca só a parte antes da primeira "/" e mantém o modelo. Vazio = provedor do próprio modelo.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <span>Provedor em todas as áreas:</span>
            <input
              value={allPrefix}
              onChange={e => setAllPrefix(e.target.value)}
              placeholder="ex.: codex"
              className="w-40 px-2 py-1 border border-gray-300 rounded-md font-mono text-xs"
            />
            <button
              type="button"
              onClick={() => setPrefixes(Object.fromEntries(table.areas.map(a => [a.key, allPrefix.trim()])))}
              className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Aplicar a todas
            </button>
            <span className="text-gray-400">(depois clique em Salvar; vazio + aplicar = limpa todas)</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
        <button
          onClick={save}
          disabled={!dirty || saving || missingFamily.length > 0}
          className="px-4 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {saving ? 'Salvando...' : 'Salvar modelos'}
        </button>
        {missingFamily.length > 0 && (
          <span className="text-[11px] text-red-600">Escolha "Cobrar como" em: {missingFamily.map(a => a.label).join(', ')}</span>
        )}
        </div>
      </div>
      <div className="p-4 space-y-6">
        {GROUP_ORDER.map(group => {
          const rows = table.areas.filter(a => a.group === group);
          if (!rows.length) return null;
          return (
            <div key={group}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{GROUP_LABELS[group]}</h3>
              <div className="grid gap-3 md:grid-cols-2">
                {rows.map(a => {
                  const value = values[a.key] || '';
                  const free = isCustom(a);
                  return (
                    <label key={a.key} className="block rounded-md border border-gray-100 p-3">
                      <span className="block text-sm font-medium text-gray-800">{a.label}</span>
                      <span className="block text-[11px] text-gray-400">{a.hint}</span>
                      {free ? (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center gap-2">
                            <input
                              value={value}
                              onChange={e => setValues(v => ({ ...v, [a.key]: e.target.value }))}
                              placeholder="ex.: avello/claude-opus-5.5"
                              aria-label={`Id do modelo — ${a.label}`}
                              className="flex-1 px-2 py-1.5 border border-gray-300 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => { setCustom(c => ({ ...c, [a.key]: false })); setValues(v => ({ ...v, [a.key]: '' })); }}
                              className="text-[11px] text-blue-600 hover:underline whitespace-nowrap"
                            >
                              Voltar à lista
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-500 whitespace-nowrap">Cobrar como</span>
                            <select
                              value={modelFams[value.trim()] || ''}
                              disabled={!value.trim()}
                              onChange={e => setModelFams(f => ({ ...f, [value.trim()]: e.target.value }))}
                              aria-label={`Família de preço — ${a.label}`}
                              className={`px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${value.trim() && !modelFams[value.trim()] ? 'border-red-400' : 'border-gray-300'}`}
                            >
                              <option value="">Escolha…</option>
                              {(table.familyOptions || []).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                            </select>
                            <span className="text-[10px] text-gray-400">tarifa da família na tabela de custos</span>
                          </div>
                        </div>
                      ) : (
                      <select
                        value={value}
                        onChange={e => {
                          const v = e.target.value;
                          if (v === CUSTOM) { setCustom(c => ({ ...c, [a.key]: true })); setValues(x => ({ ...x, [a.key]: '' })); return; }
                          setValues(x => ({ ...x, [a.key]: v }));
                        }}
                        className="mt-2 w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Padrão{a.source !== 'superadmin' && a.model ? ` (${a.model})` : a.defaultModel ? ` (${a.defaultModel})` : ''}</option>
                        {families.map(f => (
                          <optgroup key={f} label={f}>
                            {table.modelOptions.filter(o => o.family === f).map(o => (
                              <option key={o.id} value={o.id}>{o.label} — {o.id}</option>
                            ))}
                          </optgroup>
                        ))}
                        <option value={CUSTOM}>Outro id (digitar)…</option>
                      </select>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[11px] text-gray-500 whitespace-nowrap">Provedor</span>
                        <input
                          value={prefixes[a.key] || ''}
                          onChange={e => setPrefixes(p => ({ ...p, [a.key]: e.target.value }))}
                          placeholder={(a.model || '').split('/')[0] || 'cx'}
                          className="w-32 px-2 py-1 border border-gray-300 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <code className="text-[10px] text-gray-500 truncate" title={finalModel(a)}>→ {finalModel(a) || '—'}</code>
                      </div>
                      <span className="mt-1 block text-[11px] text-gray-400">
                        Em uso: <code className="text-[10px]">{a.model || '—'}</code>
                        {a.source === 'superadmin' ? ' (super admin)' : a.source === 'stack' ? ' (stack)' : ' (padrão)'}
                        {a.prefix ? ` · provedor ${a.prefix}` : ''}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
